import { HttpClient } from '@angular/common/http';
import { catchError, concatMap, EMPTY, first, map, Observable, Observer, tap, throwError } from 'rxjs';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from 'src/app/shared/confirmation-dialog/confirmation-dialog.component';
import { ToastService } from '@app/core/services/toast.service';
import { DialogConfig, DialogService } from '@app/core/services/dialog.service';

export class BaseService {
  constructor(
    protected http: HttpClient,
    protected baseUrl: string,
    protected toastService: ToastService,
    protected dialogService: DialogService,
  ) {}

  /**
   * http get command
   * @deprecated bitte nutzt stattdessen: this.handleHttp<[TYPE]>(this.https.get<[TYPE]>(this.buildPath([PATH])), {error?: string, success?: string})
   * @param path path
   * @param serverResponseMessage serverResponseMessage
   * @returns Observable mit generic type
   */
  get<T>(path: string, serverResponseMessage?: ServerResponseMessageModel<T>): Observable<T> {
    return this.tapToast(this.http.get<T>(this.baseUrl + path), serverResponseMessage);
  }

  /**
   * http post command
   * @deprecated bitte nutzt stattdessen: this.handleHttp<[TYPE]>(this.https.post<[TYPE]>(this.buildPath([PATH]), [BODY]), {error?: string, success?: string})
   * @param path path
   * @param body body
   * @param serverResponseMessage serverResponseMessage
   * @returns Observable mit generic type
   */
  post<T>(path: string, body?: unknown, serverResponseMessage?: ServerResponseMessageModel<T>): Observable<T> {
    return this.tapToast(this.http.post<T>(this.baseUrl + path, body), serverResponseMessage);
  }

  /**
   * http patch command
   * @deprecated bitte nutzt stattdessen: this.handleHttp<[TYPE]>(this.https.patch<[TYPE]>(this.buildPath([PATH]), [BODY]), {error?: string, success?: string})
   * @param path path
   * @param body body
   * @param serverResponseMessage serverResponseMessage
   * @returns Observable mit generic type
   */
  patch<T>(path: string, body?: unknown, serverResponseMessage?: ServerResponseMessageModel<T>): Observable<T> {
    return this.tapToast(this.http.patch<T>(this.baseUrl + path, body), serverResponseMessage);
  }

  /**
   * http put command
   * @deprecated bitte nutzt stattdessen: this.handleHttp<[TYPE]>(this.https.put<[TYPE]>(this.buildPath([PATH]), [BODY]), {error?: string, success?: string})
   * @param path path
   * @param body body
   * @param serverResponseMessage serverResponseMessage
   * @returns Observable mit generic type
   */
  put<T>(path: string, body?: unknown, serverResponseMessage?: ServerResponseMessageModel<T>): Observable<T> {
    return this.tapToast(this.http.put<T>(this.baseUrl + path, body), serverResponseMessage);
  }

  /**
   * http delete command
   * @deprecated bitte nutzt stattdessen: this.handleHttp<[TYPE]>(this.https.delete<[TYPE]>(this.buildPath([PATH])), {error?: string, success?: string})
   * @param path path
   * @param serverResponseMessage serverResponseMessage
   * @param confirmationDialogModel dialogConfigData
   * @returns Observable mit generic type
   */
  delete<T>(
    path: string,
    serverResponseMessage?: ServerResponseMessageModel<T>,
    confirmationDialogModel?: ConfirmationDialogModel,
  ): Observable<T> {
    return this.preConcatConfirmationDialog(
      this.tapToast(this.http.delete<T>(this.baseUrl + path), serverResponseMessage),
      confirmationDialogModel,
    );
  }

  buildPath(path: string): string {
    return this.baseUrl + path;
  }

  handleHttp<T>(
    observable: Observable<T>,
    message: {
      error?: string;
      success?: string;
    } = {},
    confirmationDialogConfig?: ConfirmationDialogModel,
  ): Observable<T> {
    return this.preConcatConfirmationDialog(
      observable.pipe(
        first(),
        tap(_ => {
          if (message.success) {
            this.toastService.success(message.success);
          }
        }),
        map(response => response),
        catchError(error => {
          this.toastService.error(message.error ?? error.message);
          return throwError(() => error);
        }),
      ),
      confirmationDialogConfig,
    );
  }

  protected preConcatConfirmationDialog<T>(observable: Observable<T>, confirmationDialogModel?: ConfirmationDialogModel): Observable<T> {
    if (!confirmationDialogModel) {
      return observable;
    }
    return this.openConfirmationDialog(confirmationDialogModel).pipe(
      concatMap(confirmation => {
        if (confirmation) {
          return observable;
        }
        return EMPTY;
      }),
    );
  }

  private tapToast<T>(observable: Observable<T>, serverResponseMessage?: ServerResponseMessageModel<T>): Observable<T> {
    if (!serverResponseMessage) {
      return observable;
    }
    return observable.pipe(
      tap<T>({
        next: (value: T) => {
          if (serverResponseMessage.successMessage) {
            this.toastService.success(serverResponseMessage.successMessage(value));
          }
        },
        error: (err: unknown) => {
          if (serverResponseMessage.errorMessage) {
            this.toastService.error(serverResponseMessage.errorMessage(err));
          }
        },
      } as Observer<T>),
    );
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public openConfirmationDialog(data: ConfirmationDialogModel): Observable<boolean> {
    const dialogConfig: DialogConfig = { data };
    const dialogRef = this.dialogService.open(ConfirmationDialogComponent, dialogConfig);
    return dialogRef.afterClosed();
  }
}

export interface ServerResponseMessageModel<T> {
  successMessage: (value?: T) => string;
  errorMessage: (error?: unknown) => string;
}
